import { PERIODS } from './dashboardConstants.js';

/**
 * 期間の範囲（SCR-P-001 IN-P-003・BTN-P-006・007、§5.1 #3〜#6、§6.5 29〜33。#661）
 *
 * 日付は JST の `YYYY-MM-DD` の文字列で持つ（API の from・to と同じ形）。
 * 計算はブラウザのタイムゾーンに依らないよう、日付を UTC の 0 時として扱う。
 */

const DAY_MS = 24 * 60 * 60 * 1000;
/** JST は UTC から固定 +9 時間（日本に夏時間は無い） */
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

/** 範囲の長さの上限（§5.1 #4。PM の決定 2026-10-29、#661 comment 6485292094 の 3 の b） */
export const RANGE_LIMITS = {
    WEEKS: 26,
    MONTHS: 24,
};

/** 誤りの文（§5.1 #3〜#6。並びは確かめる順 = 出す順） */
export const RANGE_ERRORS = {
    BOTH_REQUIRED: '開始日と終了日の両方を入れてください',
    TOO_LONG_WEEKLY: '週次は 26 週までの期間を指定してください',
    TOO_LONG_MONTHS: '期間は 24 か月以内で指定してください',
    AFTER_TODAY: '終了日は今日までの日付を入れてください',
    END_BEFORE_START: '終了日は開始日以降の日付を入れてください',
};

/** 一覧の補足の文（§6.5 29） */
export const RANGE_HINT = '週次は 26 週まで、月次・四半期は 24 か月まで指定できます';

const pad2 = (n) => String(n).padStart(2, '0');

const YMD_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * `YYYY-MM-DD` を UTC 0 時のミリ秒にする。形が違う・無い日付（2 月 30 日等）は NaN
 *
 * @param {string} ymd
 * @returns {number}
 */
function toUtcMs(ymd) {
    const m = typeof ymd === 'string' ? YMD_PATTERN.exec(ymd) : null;
    if (!m) return NaN;
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const ms = Date.UTC(y, mo - 1, d);
    const back = new Date(ms);
    if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) return NaN;
    return ms;
}

const fromUtcMs = (ms) => {
    const d = new Date(ms);
    return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
};

/** 日付として読める `YYYY-MM-DD` か */
export function isValidYmd(ymd) {
    return !Number.isNaN(toUtcMs(ymd));
}

/**
 * 今日（JST）の `YYYY-MM-DD`
 *
 * @param {number} [nowMs] - 試験で時刻を固定するため
 */
export function todayJst(nowMs = Date.now()) {
    return fromUtcMs(Math.floor((nowMs + JST_OFFSET_MS) / DAY_MS) * DAY_MS);
}

/**
 * 範囲に重なる週の数（週は月曜始まり。集計 API 設計 §2.2）
 */
export function countWeeks(from, to) {
    const fromMs = toUtcMs(from);
    const toMs = toUtcMs(to);
    // 1970-01-01 は木曜。月曜を 0 とする曜日の番号（1970 年より前は % 7 が負になるため 7 を足してから取り直す）
    const weekday = (((Math.floor(fromMs / DAY_MS) + 3) % 7) + 7) % 7;
    const mondayMs = fromMs - weekday * DAY_MS;
    return Math.floor((toMs - mondayMs) / (7 * DAY_MS)) + 1;
}

/** 範囲に重なる月の数 */
export function countMonths(from, to) {
    const [fy, fm] = from.split('-').map(Number);
    const [ty, tm] = to.split('-').map(Number);
    return (ty - fy) * 12 + (tm - fm) + 1;
}

/** 週次で出せる範囲か（§6.5 31。範囲が無ければ常に出せる） */
export function isWeeklyAllowed(range) {
    return !range || countWeeks(range.from, range.to) <= RANGE_LIMITS.WEEKS;
}

/**
 * 入力の誤りを確かめる（§5.1 #3〜#6）。誤りが複数あるときは番号の小さい 1 つだけを返す
 *
 * 両方とも空のときは誤りの文を出さない（指定なしと同じ。「適用」は押せなくする。§5.1 #3）。
 *
 * @param {{from: string, to: string}} draft - 日付の入力の値（`<input type="date">` は読めない日付を空にする）
 * @param {string} period - CD-005
 * @param {string} today - todayJst()
 * @returns {{error: string|null, canApply: boolean}}
 */
export function validateRange({ from, to }, period, today) {
    const hasFrom = isValidYmd(from);
    const hasTo = isValidYmd(to);
    if (!hasFrom && !hasTo) return { error: null, canApply: false };
    if (!hasFrom || !hasTo) return { error: RANGE_ERRORS.BOTH_REQUIRED, canApply: false };

    let error = null;
    if (period === PERIODS.WEEKLY && countWeeks(from, to) > RANGE_LIMITS.WEEKS) {
        error = RANGE_ERRORS.TOO_LONG_WEEKLY;
    } else if (period !== PERIODS.WEEKLY && countMonths(from, to) > RANGE_LIMITS.MONTHS) {
        error = RANGE_ERRORS.TOO_LONG_MONTHS;
    } else if (toUtcMs(to) > toUtcMs(today)) {
        error = RANGE_ERRORS.AFTER_TODAY;
    } else if (toUtcMs(from) > toUtcMs(to)) {
        error = RANGE_ERRORS.END_BEFORE_START;
    }
    return { error, canApply: error === null };
}

/**
 * 「今月」「今年」（BTN-P-007。§6.5 30）。範囲と、切り替える単位を返す
 *
 * - 今月: 今月 1 日〜今日、週次（月次では 1 期間だけになるため。PM の決定 3 の d）
 * - 今年: 1 月 1 日〜今日、月次（週次では 26 週を超えうるため）
 */
// 仮定: 「今年」は暦の年（1〜12 月）とする（未確認。BIGTREE 様に確認する: #661 comment 6485292094 の 4）
export const RANGE_PRESETS = [
    {
        key: 'thisMonth',
        label: '今月',
        build: (today) => ({ range: { from: `${today.slice(0, 7)}-01`, to: today }, period: PERIODS.WEEKLY }),
    },
    {
        key: 'thisYear',
        label: '今年',
        build: (today) => ({ range: { from: `${today.slice(0, 4)}-01-01`, to: today }, period: PERIODS.MONTHLY }),
    },
];

/** 範囲の表示「2026/10/01〜2026/10/29」（BTN-P-006・DP-P-013） */
export function formatRange(range) {
    return `${range.from.replace(/-/g, '/')}〜${range.to.replace(/-/g, '/')}`;
}
